// Validation result, lost on refresh

import { WorksheetJoin } from "../engine/joinSources";
import { MappedSheet, SkippedRow, ValidationIssue, WorksheetRows } from "../engine/validationTypes";

export type ValidationRunData = {
  runID: number;
  // Rebuilt from the saved issue list after a refresh
  fromCheckpoint: boolean;
  sheets: Map<string, MappedSheet>;
  // Inline edits change these rows in place
  rawSheets: WorksheetRows[];
  issues: ValidationIssue[];
  // Sorted by row number
  issuesByWorksheet: Map<string, number[]>;
  // Indexes into MappedSheet.rowNumbers
  errorRowIndexesByWorksheet: Map<string, number[]>;
  // Issue IDs for each cell, looked up with cellKey()
  issuesByCell: Map<string, number[]>;
  skippedRowsByWorksheet: Map<string, SkippedRow[]>;
  // Kept so a re-run on the already-joined rows still reports what the join left out
  worksheetJoins: WorksheetJoin[];
};

export const cellKey = (worksheet: string, rowNumber: number, field: string) =>
  `${worksheet}\u0000${rowNumber}\u0000${field}`;

type RunDataOptions = {
  fromCheckpoint?: boolean;
  skippedRowsByWorksheet?: Map<string, SkippedRow[]>;
  worksheetJoins?: WorksheetJoin[];
};

export function buildValidationRunData(
  runID: number,
  sheets: MappedSheet[],
  rawSheets: WorksheetRows[],
  issues: ValidationIssue[],
  { fromCheckpoint = false, skippedRowsByWorksheet = new Map(), worksheetJoins = [] }: RunDataOptions = {}
): ValidationRunData {
  const sheetsByWorksheet = new Map(sheets.map((sheet) => [sheet.worksheet, sheet]));
  const rowIndexByNumber = new Map(
    sheets.map((sheet) => [sheet.worksheet, new Map(sheet.rowNumbers.map((rowNumber, index) => [rowNumber, index]))])
  );

  const issuesByWorksheet = new Map<string, number[]>();
  const errorRowIndexes = new Map<string, Set<number>>();
  const issuesByCell = new Map<string, number[]>();

  for (const issue of issues) {
    const worksheetIssues = issuesByWorksheet.get(issue.worksheet);
    if (worksheetIssues) worksheetIssues.push(issue.issueID);
    else issuesByWorksheet.set(issue.worksheet, [issue.issueID]);

    const rowIndex = rowIndexByNumber.get(issue.worksheet)?.get(issue.rowNumber);
    if (rowIndex !== undefined) {
      const rows = errorRowIndexes.get(issue.worksheet);
      if (rows) rows.add(rowIndex);
      else errorRowIndexes.set(issue.worksheet, new Set([rowIndex]));
    }

    if (issue.field) {
      const key = cellKey(issue.worksheet, issue.rowNumber, issue.field);
      const cellIssues = issuesByCell.get(key);
      if (cellIssues) cellIssues.push(issue.issueID);
      else issuesByCell.set(key, [issue.issueID]);
    }
  }

  for (const issueIDs of issuesByWorksheet.values()) {
    issueIDs.sort((a, b) => issues[a].rowNumber - issues[b].rowNumber || a - b);
  }

  return {
    runID,
    fromCheckpoint,
    sheets: sheetsByWorksheet,
    rawSheets,
    issues,
    issuesByWorksheet,
    errorRowIndexesByWorksheet: new Map(
      [...errorRowIndexes.entries()].map(([worksheet, rows]) => [worksheet, [...rows].sort((a, b) => a - b)])
    ),
    issuesByCell,
    skippedRowsByWorksheet,
    worksheetJoins,
  };
}

const runsBySubmission = new Map<string, ValidationRunData>();

export function setValidationRun(submissionID: string, data: ValidationRunData): void {
  runsBySubmission.set(submissionID, data);
}

export function getValidationRun(submissionID: string): ValidationRunData | undefined {
  return runsBySubmission.get(submissionID);
}

export function clearValidationRun(submissionID: string): void {
  runsBySubmission.delete(submissionID);
}
